import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from "@/utils/appToast";
import { markWorkerJobReached } from '@/utils/markJobReached';
import {
  ARRIVAL_CONSECUTIVE_READINGS,
  ARRIVAL_MIN_ACCURACY_METERS,
  ARRIVAL_RADIUS_METERS,
  geoJsonToPoint,
  getDistanceMeters,
  isWithinRadiusMeters,
  type GeoPoint,
} from '@/utils/geo';

export type OnMyWayJob = {
  _id: string;
  status: string;
  job?: {
    _id?: string;
    location?: {
      coordinates?: [number, number];
    };
    user?: { _id?: string };
  };
};

type UseAutoMarkReachedOptions = {
  jobs: OnMyWayJob[];
  enabled?: boolean;
  onReached?: () => void;
};

type JobTrackingState = {
  consecutiveInRadius: number;
  triggered: boolean;
};

export function useAutoMarkReached({
  jobs,
  enabled = true,
  onReached,
}: UseAutoMarkReachedOptions) {
  const { t } = useTranslation();
  const [gpsAvailable, setGpsAvailable] = useState(true);
  const [workerPosition, setWorkerPosition] = useState<GeoPoint | null>(null);
  const [distancesByJobId, setDistancesByJobId] = useState<Record<string, number>>({});
  const trackingRef = useRef<Record<string, JobTrackingState>>({});
  const watchIdRef = useRef<number | null>(null);
  const onReachedRef = useRef(onReached);
  onReachedRef.current = onReached;

  const activeJobs = jobs.filter(
    (j) => j.status === 'on_my_way' && geoJsonToPoint(j.job?.location?.coordinates)
  );

  const markJobReached = useCallback(
    async (jobApplicationId: string, coords: GeoPoint, isAuto = true) => {
      const result = await markWorkerJobReached(jobApplicationId, coords);

      if (result.success) {
        if (isAuto) {
          toast.success(t('autoArrivalDetected'));
        }
        onReachedRef.current?.();
        return true;
      }

      return false;
    },
    [t]
  );

  useEffect(() => {
    if (!enabled || activeJobs.length === 0 || !('geolocation' in navigator)) {
      if (!('geolocation' in navigator)) setGpsAvailable(false);
      return;
    }

    const activeIds = new Set(activeJobs.map((j) => j._id));
    Object.keys(trackingRef.current).forEach((id) => {
      if (!activeIds.has(id)) delete trackingRef.current[id];
    });

    activeJobs.forEach((job) => {
      if (!trackingRef.current[job._id]) {
        trackingRef.current[job._id] = { consecutiveInRadius: 0, triggered: false };
      }
    });

    const handlePosition = async (position: GeolocationPosition) => {
      const worker: GeoPoint = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      const accuracy = position.coords.accuracy;
      setGpsAvailable(true);
      setWorkerPosition(worker);

      const nextDistances: Record<string, number> = {};

      for (const job of activeJobs) {
        const destination = geoJsonToPoint(job.job?.location?.coordinates);
        if (!destination) continue;

        const distance = getDistanceMeters(worker, destination);
        nextDistances[job._id] = distance;

        const state = trackingRef.current[job._id];
        if (!state || state.triggered) continue;

        const inRadius = isWithinRadiusMeters(worker, destination, ARRIVAL_RADIUS_METERS);
        const accurateEnough = accuracy <= ARRIVAL_MIN_ACCURACY_METERS;

        if (inRadius && accurateEnough) {
          state.consecutiveInRadius += 1;
        } else {
          state.consecutiveInRadius = 0;
        }

        if (state.consecutiveInRadius >= ARRIVAL_CONSECUTIVE_READINGS) {
          state.triggered = true;
          const ok = await markJobReached(job._id, worker, true);
          if (!ok) {
            state.triggered = false;
            state.consecutiveInRadius = 0;
          }
        }
      }

      setDistancesByJobId((prev) => ({ ...prev, ...nextDistances }));
    };

    const handleError = () => {
      setGpsAvailable(false);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, activeJobs.map((j) => j._id).join(','), markJobReached]);

  return {
    gpsAvailable,
    workerPosition,
    distancesByJobId,
    markJobReached,
    hasActiveTracking: activeJobs.length > 0 && enabled,
  };
}
